import React, { useEffect, useState } from 'react';
import { Form, Input, Button, DatePicker, Select, message } from 'antd';
import axios from 'axios';
import dayjs from 'dayjs';
import { DeleteOutlined } from '@ant-design/icons';

const { Option } = Select;

const CareerForm = ({ initialValues = null, onSubmit, editMode }) => {
  const [categories, setCategories] = useState([]);
  const [form] = Form.useForm();
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/category_jobs`);
        setCategories(res.data.data || []);
      } catch (err) {
        console.error("Error fetching categories:", err);
      }
    };
    fetchCategories();
  }, [BASE_URL]);

  // Set data saat edit
  useEffect(() => {
    if (initialValues) {
      // Pastikan description dan qualification selalu array
      let description = [];
      let qualification = [];

      // Parsing description
      if (initialValues.description) {
        if (typeof initialValues.description === 'string') {
          try {
            description = JSON.parse(initialValues.description);
          } catch {
            description = [initialValues.description];
          }
        } else if (Array.isArray(initialValues.description)) {
          description = initialValues.description;
        } else {
          description = [initialValues.description];
        }
      }

      // Parsing qualification
      if (initialValues.qualification) {
        if (typeof initialValues.qualification === 'string') {
          try {
            qualification = JSON.parse(initialValues.qualification);
          } catch {
            qualification = [initialValues.qualification];
          }
        } else if (Array.isArray(initialValues.qualification)) {
          qualification = initialValues.qualification;
        } else {
          qualification = [initialValues.qualification];
        }
      }

      form.setFieldsValue({
        title: initialValues.title || '',
        location: initialValues.location || '',
        categoryId: initialValues.categoryId || null,
        type: initialValues.type || '',
        company: initialValues.company || '',
        description,
        qualification,
        published: initialValues.published ? dayjs(initialValues.published) : null,
        deadline: initialValues.deadline ? dayjs(initialValues.deadline) : null,
      });
    } else {
      form.resetFields();
    }
  }, [initialValues, form]);

  const handleSubmit = async (values) => {
    const payload = {
      ...values,
      published: values.published ? values.published.toISOString() : null,
      deadline: values.deadline ? values.deadline.toISOString() : null,
    };

    try {
      await onSubmit(payload);
      form.resetFields();
    } catch (err) {
      console.error(err);
      message.error("Error saving job");
    }
  };

  return (
    <Form form={form} layout="vertical" onFinish={handleSubmit}>
      <Form.Item label="Title" name="title" rules={[{ required: true, message: 'Input Title Jobs!' }]}>
        <Input placeholder="exp: Desain Grafis" />
      </Form.Item>

      <Form.Item label="Location" name="location" rules={[{ required: true, message: 'Input Location Jobs!' }]}>
        <Input placeholder="exp: Tangerang" />
      </Form.Item>

      <Form.Item label="Category" name="categoryId" rules={[{ required: true, message: 'Choose Category Jobs!' }]}>
        <Select placeholder="Choose Category Jobs">
          {categories.map(cat => (
            <Option key={cat.id} value={cat.id}>{cat.name}</Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item label="Types" name="type" rules={[{ required: true, message: 'Choose Type Jobs!' }]}>
        <Select placeholder="Choose Type Jobs!">
          <Option value="Full-Time">Full-Time</Option>
          <Option value="Part-Time">Part-Time</Option>
          <Option value="Freelance">Freelance</Option>
          <Option value="Internship">Internship</Option>
        </Select>
      </Form.Item>

      <Form.Item label="Company" name="company" rules={[{ required: true, message: 'Input Company!' }]}>
        <Input placeholder="exp: PT Bolde Inovasi Global" />
      </Form.Item>

      {/* Description */}
      <div className="mb-4">
        <label className="block mb-2 font-medium">Description</label>
        <Form.List name="description" rules={[{ required: true, message: 'Enter at least 1 point!' }]}>
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <div key={key} className="flex items-center gap-2 mb-2">
                  <Form.Item
                    {...restField}
                    name={name}
                    rules={[{ required: true, message: 'Enter description point!' }]}
                    className="flex-1"
                  >
                    <Input
                      placeholder="Enter description point!"
                      suffix={
                        <DeleteOutlined
                          onClick={() => remove(name)}
                          style={{ color: '#ec4899', cursor: 'pointer' }}
                        />
                      }
                    />
                  </Form.Item>
                </div>
              ))}
              <Form.Item>
                <Button
                  type="primary"
                  onClick={() => add()}
                  style={{ backgroundColor: "#ec4899", borderColor: '#ec4899' }}
                >
                  Tambah Poin
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>
      </div>

      {/* Qualification */}
      <div className="mb-4">
        <label className="block mb-2 font-medium">Qualification</label>
        <Form.List name="qualification" rules={[{ required: true, message: 'Enter at least 1 point!' }]}>
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <div key={key} className="flex items-center gap-2 mb-2">
                  <Form.Item
                    {...restField}
                    name={name}
                    rules={[{ required: true, message: 'Enter qualification point!' }]}
                    className="flex-1"
                  >
                    <Input
                      placeholder="Enter qualification point!"
                      suffix={
                        <DeleteOutlined
                          onClick={() => remove(name)}
                          style={{ color: '#ec4899', cursor: 'pointer' }}
                        />
                      }
                    />
                  </Form.Item>
                </div>
              ))}
              <Form.Item>
                <Button
                  type="primary"
                  onClick={() => add()}
                  style={{ backgroundColor: "#ec4899", borderColor: '#ec4899' }}
                >
                  Tambah Poin
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>
      </div>

      <Form.Item label="Published Date" name="published" rules={[{ required: true, message: 'Pilih tanggal publikasi!' }]}>
        <DatePicker style={{ width: '100%' }} />
      </Form.Item>

      <Form.Item label="Deadline Date" name="deadline" rules={[{ required: true, message: 'Pilih deadline pendaftaran!' }]}>
        <DatePicker style={{ width: '100%' }} />
      </Form.Item>

      <Form.Item style={{ textAlign: 'right' }}>
        <Button
          type="primary"
          htmlType="submit"
          style={{ backgroundColor: '#ec4899', borderColor: '#ec4899' }}
        >
          {editMode ? 'Update Career' : 'Add Career'}
        </Button>
      </Form.Item>
    </Form>
  );
};

export default CareerForm;
